/** Halloween Night wheel: Oct 31 18:00 – Nov 1 06:00 Copenhagen. Server is authoritative; this only decides what to show. */
export type NightPrizeKey =
  | "chef_master" | "jumpscare" | "credits_25" | "credits_50" | "spooky_badge" | "tokens_5" | "halloween_recipe"
  | "credits_75" | "credits_100" | "better_luck" | "jumpscare_2" | "merch_30" | "surprise" | "tokens_10" | "pro" | "night_hoodie";

/** Order and colours follow the owner's reference design (clockwise from the top pointer). */
export const NIGHT_WHEEL_PRIZES: ReadonlyArray<{ key: NightPrizeKey; labelKey: string; icon: string; tone: string }> = [
  { key: "jumpscare", labelKey: "halloweenNight.prizes.jumpscare", icon: "👻", tone: "ink" },
  { key: "credits_25", labelKey: "halloweenNight.prizes.credits25", icon: "🪙", tone: "red" },
  { key: "credits_50", labelKey: "halloweenNight.prizes.credits50", icon: "🪙", tone: "orange" },
  { key: "spooky_badge", labelKey: "halloweenNight.prizes.spookyBadge", icon: "🏅", tone: "purple" },
  { key: "tokens_5", labelKey: "halloweenNight.prizes.tokens5", icon: "🟣", tone: "indigo" },
  { key: "halloween_recipe", labelKey: "halloweenNight.prizes.recipe", icon: "🍬", tone: "green" },
  { key: "credits_75", labelKey: "halloweenNight.prizes.credits75", icon: "🪙", tone: "purple" },
  { key: "credits_100", labelKey: "halloweenNight.prizes.credits100", icon: "💰", tone: "gold" },
  { key: "better_luck", labelKey: "halloweenNight.prizes.betterLuck", icon: "🌙", tone: "ink" },
  { key: "jumpscare_2", labelKey: "halloweenNight.prizes.jumpscare", icon: "👻", tone: "indigo" },
  { key: "merch_30", labelKey: "halloweenNight.prizes.merch30", icon: "🎟️", tone: "orange" },
  { key: "night_hoodie", labelKey: "halloweenNight.prizes.nightHoodie", icon: "🧥", tone: "gold" },
  { key: "surprise", labelKey: "halloweenNight.prizes.surprise", icon: "✂️", tone: "green" },
  { key: "tokens_10", labelKey: "halloweenNight.prizes.tokens10", icon: "🪙", tone: "gold" },
  { key: "pro", labelKey: "halloweenNight.prizes.pro", icon: "👨‍🍳", tone: "red" },
  { key: "chef_master", labelKey: "halloweenNight.prizes.chefMaster", icon: "👑", tone: "purple" },
];

/** Hours between spins per plan. */
export const NIGHT_COOLDOWN_HOURS = { free: 3, pro: 2, chef_master: 1 } as const;

function copenhagenParts(now: Date) {
  const p = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Copenhagen", month: "numeric", day: "numeric", hour: "numeric", hourCycle: "h23" }).formatToParts(now);
  const get = (t: string) => Number(p.find((x) => x.type === t)?.value);
  return { month: get("month"), day: get("day"), hour: get("hour") };
}

/** Chef Master early access: Oct 31 16:00–18:00 Copenhagen (server confirms the tier). */
export function isHalloweenNightEarly(now = new Date()): boolean {
  const { month, day, hour } = copenhagenParts(now);
  return month === 10 && day === 31 && hour >= 16 && hour < 18;
}

export function isHalloweenNight(now = new Date()): boolean {
  const { month, day, hour } = copenhagenParts(now);
  return (month === 10 && day === 31 && hour >= 18) || (month === 11 && day === 1 && hour < 6);
}

/** Ms until the Halloween Night wheel opens (Oct 31 18:00 Copenhagen = 17:00 UTC, DST has ended); 0 while open or after. */
export function msUntilHalloweenNight(now = new Date()): number {
  const open = Date.UTC(now.getUTCFullYear(), 9, 31, 17, 0, 0);
  return Math.max(0, open - now.getTime());
}

/** Ms until the Halloween Night wheel closes (Nov 1 06:00 Copenhagen = 05:00 UTC); 0 when not open. */
export function msUntilHalloweenNightEnds(now = new Date()): number {
  if (!isHalloweenNight(now)) return 0;
  const y = now.getUTCMonth() === 9 ? now.getUTCFullYear() : now.getUTCFullYear();
  return Math.max(0, Date.UTC(y, 10, 1, 5, 0, 0) - now.getTime());
}
